import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Container,
  DialogContentText,
  TablePagination,
} from "@mui/material";
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import StyledTextField from "../../components/StyledComponents/StyledTextField";
import { StandardListItem, List, Card, CardHeader, Bar, Dialog, Form, FormGroup, Input, FormItem, Label } from '@ui5/webcomponents-react';


import AppLayout from "../../components/AppLayout/AppLayout";
import httpRequest from "../../httpRequest";

const SuppliersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [vendors, setVendors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vendorDetails, setVendorDetails] = useState({
    vendor_code: "",
    vendor_name: "",
    vendor_address: "",
    vendor_email: "",
	vendor_contact: "",
    account_number: "",
    iban_no: "",
    bank_name: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editVendorId, setEditVendorId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
    const [csvFile, setCsvFile] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-vendors`);
      setVendors(response.data.vendors);
    } catch (error) {
      enqueueSnackbar("Error fetching vendors", { variant: "error" });
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
    setIsEditing(false);
    setVendorDetails({
      vendor_code: "",
      vendor_name: "",
      vendor_address: "",
	  vendor_email: "",
      vendor_contact: "",
      account_number: "",
      iban_no: "",
      bank_name: "",
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVendorDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const handleAddOrUpdateVendor = async () => {
    try {
      if (isEditing) {
        await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-vendor/${editVendorId}`, vendorDetails);
        enqueueSnackbar("Vendor updated successfully", { variant: "success" });
      } else {
        await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-vendor`, vendorDetails);
        enqueueSnackbar("Vendor added successfully", { variant: "success" });
      }
      fetchVendors();
      handleDialogClose();
    } catch (error) {
      enqueueSnackbar("Vendor code or email already exists", { variant: "error" });
    }
  };

  const handleEditVendor = (vendor) => {
    setEditVendorId(vendor.id);
    setIsEditing(true);
    setVendorDetails({
      vendor_code: vendor.vendor_code,
      vendor_name: vendor.vendor_name,
      vendor_address: vendor.vendor_address,
	  vendor_email: vendor.vendor_email,
      vendor_contact: vendor.vendor_contact,
      account_number: vendor.account_number,
      iban_no: vendor.iban_no,
      bank_name: vendor.bank_name,
    });
    setDialogOpen(true);
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDeleteVendors = async () => {
    try {
      await Promise.all(
        selectedVendors.map((vendorId) => httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-vendor/${vendorId}`))
      );
      enqueueSnackbar("Vendor(s) deleted successfully", { variant: "success" });
      fetchVendors();
      setSelectedVendors([]);
      handleCloseConfirmDialog();
    } catch (error) {
      enqueueSnackbar("Error deleting vendor(s)", { variant: "error" });
    }
  };

  const handleSelectVendor = (vendorId) => {
    setSelectedVendors((prevSelected) =>
      prevSelected.includes(vendorId)
        ? prevSelected.filter((id) => id !== vendorId)
        : [...prevSelected, vendorId]
    );
  };

  const handleSelectAllVendors = () => {
    const pageVendors = filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((vendor) => vendor.id);
    if (selectedVendors.length === pageVendors.length) {
      setSelectedVendors(selectedVendors.filter((id) => !pageVendors.includes(id)));
    } else {
      setSelectedVendors([...selectedVendors, ...pageVendors.filter((id) => !selectedVendors.includes(id))]);
    }
  };

  // Function to filter vendors based on search query
  const filteredVendors = vendors.filter((vendor) => {
    const vendorCode = vendor.vendor_code ? vendor.vendor_code.toString().toLowerCase() : '';
    const vendorName = vendor.vendor_name ? vendor.vendor_name.toString().toLowerCase() : '';
    const searchLower = searchQuery.toLowerCase();
    return vendorCode.includes(searchLower) || vendorName.includes(searchLower);
  });

  // Pagination control handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
    const handleCsvFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handleImportDialogOpen = () => {
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setCsvFile(null);
  };

  const handleImportVendors = async () => {
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/import-vendors`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      enqueueSnackbar("Vendors imported successfully", { variant: "success" });
      fetchVendors();
      handleImportDialogClose();
    } catch (error) {
      enqueueSnackbar("Error importing vendors", { variant: "error" });
    }
  };

const generateSampleCsvData = () => {
  const sampleData = [
    {
      vendor_code: "V001",
      vendor_name: "Sample Vendor 1",
      vendor_address: "123 Main St",
      vendor_email: "vendor1@example.com",
      vendor_contact: "123-456-7890",
      account_number: "123456789",
      iban_no: "US1234567890",
      bank_name: "Sample Bank",
    },

  ];

  const csv = "vendor_code,vendor_name,vendor_address,vendor_email,vendor_contact,account_number,iban_no,bank_name\n" +
    sampleData.map(row => Object.values(row).join(",")).join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  return URL.createObjectURL(blob);
};

  const sampleCsvUrl = generateSampleCsvData();




  return (
    <AppLayout>
      <Container  sx={{ mt: 4, mb: 4 }}>
	  
	   <Dialog
        headerText={isEditing ? "Edit Vendor" : "Add Vendor"}
        open={dialogOpen}
        onAfterClose={handleDialogClose}
        stretch
  
        footer={
          <Bar design="Footer" endContent={
            <>
			            <ButtonOutlined style={{ marginRight: 8 }} onClick={handleDialogClose}>Cancel</ButtonOutlined>
            <ButtonContained
              onClick={handleAddOrUpdateVendor}
              {...(vendorDetails.vendor_code && vendorDetails.vendor_name ? {} : { disabled: true })}
            >
              {isEditing ? "Update" : "Add Vendor"}
            </ButtonContained>
			
			
          
            </>
          } />
        }
      >
	  <Form
          headerText="Vendor Details"
          layout="S1 M1 L2 XL2"
          labelSpan="S12 M4 L4 XL4"
        >
          <FormGroup>
            {["vendor_code", "vendor_name", "vendor_address", "vendor_email", "vendor_contact", "account_number", "iban_no", "bank_name"].map((field) => (
            <FormItem key={field}>
            <Label required={["vendor_code", "vendor_name"].includes(field)}>
              {field.replace("_", " ").split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
            </Label>
            <Input
              name={field}
              placeholder={field.replace("_", " ").split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
              value={vendorDetails[field]}
              onChange={handleInputChange}
              required={["vendor_code", "vendor_name"].includes(field)}
            />
          </FormItem>
          
            ))}
          </FormGroup>
        </Form>

        </Dialog>
	 
          

	   <Dialog
        headerText={"Confirm Delete"}
        open={confirmDialogOpen} 
        onAfterClose={handleCloseConfirmDialog}
		state="Critical"
        footer={
          <Bar design="Footer" endContent={
            <>
			            <ButtonOutlined style={{ marginRight: 8 }}  onClick={handleCloseConfirmDialog}>Cancel</ButtonOutlined>
            <ButtonContained
              onClick={handleDeleteVendors}
            >
              Delete
            </ButtonContained>
			
			
          
            </>
          } />
        }
      >
 Are you sure you want to delete the selected vendor(s)?
        </Dialog>
		
		<Dialog
          headerText="Import Vendors"
          open={importDialogOpen}
          onAfterClose={handleImportDialogClose}
          footer={
            <Bar design="Footer" endContent={
              <>
                <ButtonOutlined style={{ marginRight: 8 }} onClick={handleImportDialogClose}>Cancel</ButtonOutlined>
                <ButtonContained
                  onClick={handleImportVendors}
                  {...(csvFile ? {} : { disabled: true })}
                >
                  Import
                </ButtonContained>
              </>
            } />
          }
        >
<div>
        <p>Select a CSV file to import vendors.</p>
        <p>The file should have the following columns:</p>
        <ul>
          <li>vendor_code</li>
          <li>vendor_name</li>
          <li>vendor_address</li>
          <li>vendor_email</li>
          <li>vendor_contact</li>
          <li>account_number</li>
          <li>iban_no</li>
          <li>bank_name</li>
        </ul>
        <p>
          <a href={sampleCsvUrl} download="sample_format.csv">
            <ButtonContained>Download Sample CSV</ButtonContained>
          </a>
        </p>
        <input type="file" accept=".csv" onChange={handleCsvFileChange} />
      </div>
        </Dialog>

    <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>

		<Card header={<CardHeader subtitleText="Manage vendor information and add new vendors" 
 titleText="All Vendors" />} style={{ width: "100%", overflow: "hidden" }}>


 <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
  <input
type="text"              placeholder="Search"
              variant="outlined"
              size="small"
                        style={{ flex: "1", padding: "10px", marginRight: "20px" }}

              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
			
 <ButtonOutlined
                      onClick={handleOpenConfirmDialog}
                      {...(selectedVendors.length === 0 ? { disabled: true } : {})}
                    >
                      <DeleteIcon /> Delete
                    </ButtonOutlined>


                    <ButtonContained   style={{ marginLeft: 8 }} onClick={handleDialogOpen}>
                      <AddIcon /> Add Vendor
                    </ButtonContained>
					
					
					<ButtonContained style={{ marginLeft: 8 }} onClick={handleImportDialogOpen}>
                <UploadFileIcon /> Import Vendors
              </ButtonContained>
					
					</div>
 
          <TableContainer style={{ padding: "10px" }}>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedVendors.length > 0 && selectedVendors.length < filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                      }
                      checked={
                        filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 &&
                        selectedVendors.length === filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                      }
                      onChange={handleSelectAllVendors}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold" }}>Vendor Code</TableCell>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold" }}>Vendor Name</TableCell>
				  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold" }}>Vendor Contact</TableCell>
				  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold" }}>Vendor Email</TableCell>
                  <TableCell align="right">
                    
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((vendor) => (
                  <TableRow key={vendor.id} selected={selectedVendors.includes(vendor.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedVendors.includes(vendor.id)}
                        onChange={() => handleSelectVendor(vendor.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{vendor.vendor_code}</TableCell>
                    <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{vendor.vendor_name}</TableCell>
					<TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{vendor.vendor_contact}</TableCell>
					<TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{vendor.vendor_email}</TableCell>
                    <TableCell align="right">
                      <Button onClick={() => handleEditVendor(vendor)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={filteredVendors.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
		  </Card>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default SuppliersPage;
